/**
 * DORI Zone calculation per EN 62676-4:2014
 * Detect / Observe / Recognize / Identify
 *
 * Pixel density (px/m) decreases with distance from camera.
 * At specific thresholds different tasks become possible.
 */

export interface DORIZone {
  type: 'detect' | 'observe' | 'recognize' | 'identify';
  maxDistance: number; // meters
  color: string;
  opacity: number;
  threshold: number; // px/m
}

// EN 62676-4 thresholds (pixels per meter)
export const DORI_THRESHOLDS = {
  detect: 25,
  observe: 62.5,
  recognize: 125,
  identify: 250,
} as const;

export const DORI_COLORS = {
  detect: '#22c55e',    // green
  observe: '#eab308',   // yellow
  recognize: '#f97316', // orange
  identify: '#ef4444',  // red
} as const;

// Sensor horizontal width in mm — the SINGLE SOURCE OF TRUTH for sensor optics
// across the app: the DORI/OODPCVS distance math here AND the lens HFOV↔focal
// math in lensMath.ts (which imports this). A second, slightly different copy
// used to live in lensMath.ts, so the focal↔HFOV↔DORI pipeline silently
// disagreed for ~5 formats. The conflicts were resolved to these published
// EN 62676-4 / DORI values (product decision 2026-05-30) so DORI numbers in
// existing client reports are unchanged; the extra formats came from the former
// lensMath table. Ordered by width ascending.
export const SENSOR_SIZES: Record<string, number> = {
  '1/4"': 3.6,
  '1/3.6"': 4.0,
  '1/3.2"': 4.5,
  '1/3"': 4.8,
  '1/2.9"': 5.0,
  '1/2.8"': 5.08,
  '1/2.7"': 5.37,
  '1/2.5"': 5.76,
  '1/2.3"': 6.2,
  '1/2"': 6.4,
  '1/1.9"': 6.9,
  '1/1.8"': 7.11,
  '1/1.7"': 7.6,
  '1/1.5"': 8.6,
  '2/3"': 8.8,
  '1/1.2"': 10.4,
  '1"': 12.8,
};

// Common resolution presets: label -> horizontal pixels
export const RESOLUTION_PRESETS: { label: string; value: number }[] = [
  { label: '1080p (2MP)', value: 1920 },
  { label: '4MP', value: 2560 },
  { label: '4K (8MP)', value: 3840 },
  { label: '8K (32MP)', value: 7680 },
];

/**
 * Calculate focal length from HFOV and sensor width.
 * focalLength = (sensorWidth / 2) / tan(hfov / 2)
 *
 * For panoramic/fisheye cameras (hfov >= 180°), the rectilinear formula
 * breaks down (tan approaches infinity). We use an equidistant projection
 * approximation instead: focalLength = imageCircleDiameter / (2 * hfovRad)
 * which gives a usable (though approximate) result for DORI calculations.
 */
/**
 * How the lens maps angle to pixels. Panoramic, fisheye and surround optics are
 * equidistant: the image is an arc, not a flat plane.
 */
export type LensProjection = 'rectilinear' | 'equidistant';

/**
 * Pixels per metre of scene along one axis, at a distance.
 *
 * Two laws, because two kinds of lens:
 *   rectilinear  res / (2 d tan(theta/2))   flat image plane
 *   equidistant  res / (d theta_rad)        arc, what a panoramic sensor images
 *
 * The flat-plane form is not merely imprecise at wide angles, it is undefined:
 * tan(90 deg) is infinite, so a 180 deg panoramic camera came out of it with
 * ZERO pixels per metre and the resolution panel declared it blind. Below about
 * 20 deg the two agree to under a percent, which is what makes switching by
 * lens class safe rather than needing a hard angle cutoff.
 *
 * Lives here, beside calculateDORIZones, because the map arc and the panel
 * read-out have to be the same law or the number under the cursor contradicts
 * the ring the cursor is on.
 */
export function pxPerMeterAxis(
  distanceM: number,
  fovDeg: number,
  resPx: number,
  projection: LensProjection = 'rectilinear',
): number {
  if (distanceM <= 0.01) return resPx;
  if (!Number.isFinite(fovDeg) || fovDeg <= 0) return 0;
  if (projection === 'equidistant') {
    const arcM = (distanceM * Math.min(fovDeg, 360) * Math.PI) / 180;
    return arcM > 0 ? resPx / arcM : 0;
  }
  if (fovDeg >= 180) return 0;
  const halfRad = ((fovDeg / 2) * Math.PI) / 180;
  const extentM = 2 * distanceM * Math.tan(halfRad);
  return extentM > 0 ? resPx / extentM : 0;
}

export function calculateFocalLength(
  hfovDegrees: number,
  sensorWidthMm: number,
  projection?: LensProjection,
): number {
  // 360deg (ceiling fisheye / panoramic) is rejected on purpose: the
  // rectilinear/equidistant focal-length model degenerates there (it yields
  // centimetre-scale DORI rings). Proper fisheye DORI needs a dedicated
  // ground-projection model — tracked as a follow-up; drawing no ring is
  // better than a garbage one.
  if (hfovDegrees <= 0 || hfovDegrees >= 360 || sensorWidthMm <= 0) return 0;
  const hfovRad = (hfovDegrees * Math.PI) / 180;

  // Panoramic / fisheye lens: equidistant, because the image is an arc.
  //
  // `projection` comes from povProjection, the module whose job is deciding
  // what kind of image a device produces. Without it this fell back to a bare
  // angle test, which put the boundary in a different place from the POV and
  // the resolution panel: a two-imager 160 deg strip (Uniview IPC2K24SE,
  // 3840x1080, focal "2x4mm") is angle-linear by construction, yet a bare
  // ">= 170" called it rectilinear and the ring landed 4.06x away from the
  // number the panel printed for the same point.
  if (projection ? projection === 'equidistant' : hfovDegrees >= 170) {
    return sensorWidthMm / hfovRad;
  }

  return (sensorWidthMm / 2) / Math.tan(hfovRad / 2);
}

/**
 * Calculate DORI zone distances for a camera.
 *
 * distance = (focalLength * imageWidth) / (sensorWidth * threshold)
 */
export function calculateDORIZones(
  hfovDegrees: number,
  horizontalResolution: number = 1920,
  sensorSize: string = '1/2.8"',
  projection?: LensProjection,
): DORIZone[] {
  const sensorWidthMm = SENSOR_SIZES[sensorSize] || 5.08;
  const focalLength = calculateFocalLength(hfovDegrees, sensorWidthMm, projection);

  if (focalLength <= 0) return [];

  return (['detect', 'observe', 'recognize', 'identify'] as const).map(type => {
    const threshold = DORI_THRESHOLDS[type];
    const maxDistance = (focalLength * horizontalResolution) / (sensorWidthMm * threshold);

    return {
      type,
      maxDistance: Math.max(0, maxDistance),
      color: DORI_COLORS[type],
      opacity: 0.15,
      threshold,
    };
  });
}

/**
 * Sensor-accurate auto-range for a varifocal / motorized-zoom slider, derived
 * from the camera's PHYSICAL EN 62676-4 DORI reach — NOT a blind focal-ratio
 * multiply (which compounded a 4MP optic up to a fake 10 km).
 *
 * Returns the RECOGNIZE distance (125 px/m) — the "useful" planning range where
 * an operator can recognise a known target — hard-bounded by the DETECT
 * distance (25 px/m), the farthest the sensor produces ANY usable pixels at
 * this focal. So the auto-range can never exceed what the sensor can do.
 *
 * `hfovDegrees` is the lens HFOV at the new focal length. Returns null when the
 * optics are unknown (caller keeps the existing range). The shorter, realistic
 * range also restores blind-spot visibility (a 10 m dead-zone is ~5% of a
 * 186 m cone but ~0.1% of a 10 km one).
 */
export function recommendedDoriRangeM(
  hfovDegrees: number,
  horizontalResolution: number,
  sensorSize: string,
): number | null {
  const zones = calculateDORIZones(hfovDegrees, horizontalResolution, sensorSize);
  if (zones.length === 0) return null;
  const recognize = zones.find(z => z.type === 'recognize')?.maxDistance ?? 0;
  const detect = zones.find(z => z.type === 'detect')?.maxDistance ?? 0;
  if (recognize <= 0 || detect <= 0) return null;
  return Math.round(Math.min(recognize, detect)); // recognize ≤ detect; min is defensive
}

/**
 * Parse a resolution string like "2688x1520" or "3840x2160" into horizontal pixels.
 * Returns null if not parseable.
 */
export function parseResolutionString(resolution: string | undefined): number | null {
  if (!resolution) return null;
  const match = resolution.match(/^(\d+)\s*[x×]\s*(\d+)$/i);
  if (match) return parseInt(match[1], 10);
  return null;
}

/**
 * Snap a raw horizontal pixel count to the closest entry in
 * RESOLUTION_PRESETS. Useful when the model database exposes odd values
 * like 2688 (real Hikvision 4MP) but the UI dropdown only carries the
 * four marketing classes (2MP/4MP/4K/8K).
 */
export function snapResolutionToPreset(horizPx: number): number {
  let best = RESOLUTION_PRESETS[0].value;
  let bestDiff = Math.abs(horizPx - best);
  for (const p of RESOLUTION_PRESETS) {
    const d = Math.abs(horizPx - p.value);
    if (d < bestDiff) {
      best = p.value;
      bestDiff = d;
    }
  }
  return best;
}

/**
 * Infer a sensor size from the camera's advertised horizontal resolution.
 * Security-camera manufacturers (Dahua / Hikvision / Axis etc.) cluster
 * around a handful of sensor formats per megapixel class — this mapping is
 * a sane default when the model database doesn't carry an explicit sensor
 * field. Returned string matches one of the keys in SENSOR_SIZES so DORI
 * math can look it up directly.
 */
export function inferSensorSize(resolution: string | undefined): string {
  const horizPx = parseResolutionString(resolution);
  if (horizPx == null) return '1/2.8"';
  // Bucket by horizontal pixels. Thresholds err toward the smaller sensor
  // that's typical in consumer/SMB security gear at that MP count.
  if (horizPx >= 7000) return '1"';        // 8K / 32MP — large format
  if (horizPx >= 4000) return '1/1.8"';    // 12MP — common in flagships
  if (horizPx >= 3000) return '1/2"';      // 4K / 8MP
  if (horizPx >= 2200) return '1/2.7"';    // 4MP (2688x1520 etc.)
  return '1/2.8"';                         // 2MP / 1080p default
}

/**
 * The sensor size a coverage calculation should USE for a camera, in strict
 * provenance order:
 *   1. the user's explicit Manual override (doriSensorSize),
 *   2. the VERIFIED datasheet format (sensorFormat, filled only by the
 *      verification pipeline — manufacturer panel / script-gated harvest),
 *   3. the size inferred from the resolution class (see inferSensorSize),
 *      always labelled "(typical)" in the UI.
 *
 * This is the ONLY sanctioned fallback chain. Half the code base used
 * `?? inferSensorSize(resolution)` and the other half a hard `'1/2.8"'`,
 * so a 4K camera without an explicit doriSensorSize drew its zones with a
 * different sensor on the live map than in the exported PDF, and a customer
 * picking a specific model was still asked to "set" values the model
 * already determines (2026-08-11).
 */
export function effectiveSensorSize(
  doriSensorSize: string | undefined,
  resolution: string | undefined,
  sensorFormat?: string,
): string {
  if (doriSensorSize) return doriSensorSize;
  // A spec value with an unknown key would silently fall back to 5.08 mm in
  // the consumers — surface only formats the table can actually resolve.
  if (sensorFormat && SENSOR_SIZES[sensorFormat]) return sensorFormat;
  return inferSensorSize(resolution);
}

/**
 * Parse a resolution string into both width and height.
 * Returns null if not parseable.
 */
export function parseResolutionPair(resolution: string | undefined): { width: number; height: number } | null {
  if (!resolution) return null;
  const match = resolution.match(/^(\d+)\s*[x×]\s*(\d+)$/i);
  if (match) return { width: parseInt(match[1], 10), height: parseInt(match[2], 10) };
  return null;
}

/**
 * Choose effective horizontal resolution for DORI calculation.
 * In corridor mode, the camera sensor is rotated 90°, so pixel counts swap.
 * Falls back to 9/16 estimate when only a single horizontal value is known.
 */
export function effectiveHorizontalResolution(
  resolutionString: string | undefined,
  doriResolutionOverride: number | undefined,
  corridorMode: boolean | undefined,
): number {
  const pair = parseResolutionPair(resolutionString);
  if (corridorMode) {
    if (pair) return pair.height;
    const h = doriResolutionOverride ?? parseResolutionString(resolutionString) ?? 1920;
    return Math.round(h * 9 / 16);
  }
  if (doriResolutionOverride) return doriResolutionOverride;
  if (pair) return pair.width;
  return parseResolutionString(resolutionString) ?? 1920;
}
